import React from 'react';
import styled from 'styled-components';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
  padding: 1rem 0;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: ${props => props.active ? 'linear-gradient(to right, #00f7d2, #5ce9ff)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.active ? 'transparent' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.active ? 'black' : 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.2s;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? 'linear-gradient(to right, #00f7d2, #5ce9ff)' : 'rgba(255, 255, 255, 0.1)'};
    border-color: ${props => props.active ? 'transparent' : 'rgba(92, 233, 255, 0.5)'};
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const PageInfo = styled.span`
  color: #9ca3af;
  font-size: 0.875rem;
  margin: 0 0.5rem;
`;

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  showPageInfo = true,
}) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if we're near the start
      if (currentPage <= 3) {
        endPage = Math.min(4, totalPages - 1);
      }
      
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(totalPages - 3, 2);
      }
      
      // Add ellipsis before middle pages if needed
      if (startPage > 2) {
        pages.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis after middle pages if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();
  const startItem = totalItems > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <PaginationContainer>
      <PaginationButton
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </PaginationButton>
      
      {pageNumbers.map((page, index) => {
        if (page === '...') {
          return (
            <PageInfo key={`ellipsis-${index}`}>...</PageInfo>
          );
        }
        
        return (
          <PaginationButton
            key={page}
            active={currentPage === page}
            onClick={() => onPageChange(page)}
          >
            {page}
          </PaginationButton>
        );
      })}
      
      <PaginationButton
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </PaginationButton>
      
      {showPageInfo && totalItems > 0 && (
        <PageInfo>
          Showing {startItem}-{endItem} of {totalItems}
        </PageInfo>
      )}
    </PaginationContainer>
  );
};

export default Pagination;

